import { html, unsafeCSS } from 'lit';
import { query, customElement } from 'lit/decorators.js';
import { styles as specificAccordionStyles } from '../view/view.scss';
import { WEB_COMPONENT_POLICY_PANEL_EDR_SENSOR_GENERAL } from '../../../../../../../webcomponents.constants';
import { translateFn } from '../../../../../../../utils/translator.helper';
import { GzFormFieldWebComponent } from '../../../../../../../ui/gzForm/formField';
import { GzToggleWebComponent } from '../../../../../../../ui/gzToggle';
import { FormGroup } from '../../../../../../../ui/gzForm/formGroup/form-group';
import { ModuleId, SectionId } from '@nirvana/shared/dtos/types/policies/common';
import { GzPolicyPanelAccordionTemplateWebComponent } from '../../../../../../common/policyPanel/uiSettings/accordionTemplate';
import _ from 'lodash';
import { FormGroupValue } from '../../../../../../../ui/gzForm/formGroup/types/form-group-value';
import { PolicyPanelManager } from '../../../../PolicyPanelManager';
import deepmerge from 'deepmerge';
import {
  EdrSensorGeneralFormValue,
  EdrSensorGeneralSearchableKeywords,
} from '../../../../utils/uiSettings/edrSensor/edr-sensor.types';
import {
  EDR_SENSOR_GENERAL_DEFAULT_VALUE,
  getEdrSensorGeneralSearchableKeywords,
} from '../../../../utils/uiSettings/edrSensor/edr-sensor.values';
import { toggleDisabled } from '../../../../../../../utils/htmlElement.utils';
import { USE_EXT_JS_VERSION } from '../../../../config/config';
import { GzTextWebComponent } from '../../../../../../../ui/gzText';

@customElement(WEB_COMPONENT_POLICY_PANEL_EDR_SENSOR_GENERAL)
export class GzEdrSensorGeneralWebComponent extends GzPolicyPanelAccordionTemplateWebComponent<
  EdrSensorGeneralFormValue,
  EdrSensorGeneralSearchableKeywords
> {
  /**
   * Path for Incidents Sensor module.
   */
  protected path = {
    moduleId: ModuleId.EDR_SENSOR,
    sectionId: SectionId.GENERAL,
  };

  /**
   * Searchable keywords for Incidents Sensor section.
   */
  protected searchableKeywords = getEdrSensorGeneralSearchableKeywords();

  @query('#edr-sensor-general-toggle')
  private edrSensorToggle: GzFormFieldWebComponent<GzToggleWebComponent, boolean>;

  @query('#edr-sensor-general-info-text')
  private edrSensorInfoText: GzTextWebComponent;

  /**
   * Current accordion styles merged with template styles.
   */
  static get styles() {
    return [...GzPolicyPanelAccordionTemplateWebComponent.styles, unsafeCSS(`${specificAccordionStyles}`)];
  }

  /**
   * Render specific title.
   */
  protected renderTitle() {
    return html`<gz-form-field id="edr-sensor-general-toggle" status-field>
      <gz-toggle
        class="content"
        searchable-label="${this.searchableKeywords[EdrSensorGeneralSearchableKeywords.EdrIncidents].label}"
        gz-form-input
        @toggleChange="${this.onToggleChange}"
      >
        <span>${this.searchableKeywords[EdrSensorGeneralSearchableKeywords.EdrIncidents].translatedLabel}</span>
      </gz-toggle>
    </gz-form-field>`;
  }

  /**
   * Render specific info text.
   * @protected
   */
  protected renderInfoText() {
    return html`
      <gz-text id="edr-sensor-general-info-text" fontSize="12" fontWeight="400" class="infoText"
        >${translateFn(
          'Continuously monitors endpoint activity such as running processes, network connections, registry changes, and user behavior. This metadata is being collected, reported, and processed by machine learning algorithms and prevention technologies that detect suspicious activity on the system, and generate incidents.'
        )}
      </gz-text>
    `;
  }

  /**
   * Render specific content.
   */
  protected renderContent() {
    return html``;
  }

  /**
   * Get data from manager and set missing fields.
   * Fields used in this section:
   * - uiSettings.edrSensor.general
   */
  protected getValueFromManager(): EdrSensorGeneralFormValue {
    // get default value
    const defaultValue = {
      uiSettings: {
        [ModuleId.EDR_SENSOR]: { [SectionId.GENERAL]: EDR_SENSOR_GENERAL_DEFAULT_VALUE },
      },
    };

    // get value from manager
    const value = {
      uiSettings: {
        [ModuleId.EDR_SENSOR]: {
          [SectionId.GENERAL]: PolicyPanelManager.getInstance().getPolicy('uiSettings.edrSensor.general'),
        },
      },
    };

    // merge received value with default one
    return deepmerge(defaultValue, value);
  }

  /**
   * Create form group.
   */
  protected createFormGroup() {
    this.formGroup = FormGroup.build({
      uiSettings: {
        edrSensor: {
          general: {
            enabled: this.edrSensorToggle,
          },
        },
      },
    });
  }

  /**
   * Set form group value.
   */
  protected setFormData() {
    if (!this.valueT) {
      return;
    }

    // set form group value
    this.formGroup.value = {
      uiSettings: {
        edrSensor: {
          general: {
            enabled: this.valueT.uiSettings.edrSensor.general.enabled,
          },
        },
      },
    };
  }

  /**
   * Get form group value according to backend format.
   */
  protected getFormGroupValue(): FormGroupValue | null {
    if (!this.formGroup) {
      return null;
    }

    const value = _.cloneDeep(this.formGroup.value);

    if (USE_EXT_JS_VERSION) {
      return _.get(value, ['uiSettings', 'edrSensor'], {});
    }

    return value;
  }

  /**
   * Enable/Disable module.
   * @param event
   */
  onToggleChange(event: CustomEvent) {
    toggleDisabled(this.edrSensorInfoText, !event.detail.checked);
  }
}

declare global {
  interface HTMLElementTagNameMap {
    WEB_COMPONENT_POLICY_PANEL_EDR_SENSOR_GENERAL: GzEdrSensorGeneralWebComponent;
  }
}
